/**
 * Accounting-style number formatting.
 *
 * The client asked for plain black numbers with negatives in parentheses,
 * rather than red/green colour coding, across the Job Cost page (Angie, Note 3):
 * "I'd prefer it black and just regular for due and in parentheses for
 * overpaid, like normal numbers." This matches the legacy system, whose format
 * masks were `$#,##0.00;-$#,##0.00` (JobCost.aspx.cs:380-396).
 *
 * Colour is deliberately not a concern of these helpers — callers render plain
 * text. Sign is carried by parentheses alone, which is the convention the
 * client reads every day.
 */

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const DECIMAL = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/**
 * Format a currency amount, wrapping negatives in parentheses.
 *
 * `1234.5  -> "$1,234.50"`
 * `-1234.5 -> "($1,234.50)"`
 * `0       -> "$0.00"`
 *
 * Negative zero is treated as zero so a rounded-away loss does not render as
 * "($0.00)".
 */
export function formatAccounting(amount: number): string {
  if (Object.is(amount, -0) || amount === 0) return CURRENCY.format(0);
  return amount < 0
    ? `(${CURRENCY.format(Math.abs(amount))})`
    : CURRENCY.format(amount);
}

/**
 * Format a percentage to one decimal place, wrapping negatives in parentheses.
 *
 * `12.34  -> "12.3%"`
 * `-12.34 -> "(12.3)%"`
 *
 * The `%` sits outside the parentheses so the parenthesised part reads as the
 * number, consistent with {@link formatAccounting}.
 */
export function formatAccountingPercent(value: number): string {
  if (Object.is(value, -0) || value === 0) return `${DECIMAL.format(0)}%`;
  return value < 0
    ? `(${DECIMAL.format(Math.abs(value))})%`
    : `${DECIMAL.format(value)}%`;
}

/**
 * Format a value that is always presented as a deduction, e.g. the "Costs" row
 * of the estimator summary. Legacy used the mask `-$#,##0.00`, which renders a
 * positive cost total as a negative number, so the column reads as
 * "collected, less costs, equals balance".
 *
 * `1234.5 -> "($1,234.50)"`
 */
export function formatAccountingDeduction(amount: number): string {
  return formatAccounting(-Math.abs(amount));
}
